import * as Sentry from '@sentry/electron';

const MAX_EXCEPTIONS_PER_DAY = 100;
// Allow a maximum of 10 errors per 5 minutes to be logged to Sentry
const MAX_EXCEPTIONS_PER_CYCLE = 10;
// 5 minutes timeout before a new cycle begins
const CYCLE_EXPIRES = 5 * 60 * 1000;
// 1 day before the max exception per day resets
const DAY_EXPIRES = 24 * 60 * 60 * 1000;

type Options = {
  maxExceptionsPerCycle?: number;
  maxExceptionsPerDay?: number;
  cycleExpires?: number;
  dayExpires?: number;
};

let captureExceptionTimeout: ReturnType<typeof setTimeout> | null;
let exceptionsDayCount = 0;
let exceptionsCount = 0;
let firstDayErrorTimestamp: number | null = null;

export const captureExceptionRateLimit = (
  err: Error | unknown,
  context?: Parameters<typeof Sentry.captureException>[1],
  {
    maxExceptionsPerCycle = MAX_EXCEPTIONS_PER_CYCLE,
    maxExceptionsPerDay = MAX_EXCEPTIONS_PER_DAY,
    cycleExpires = CYCLE_EXPIRES,
    dayExpires = DAY_EXPIRES,
  }: Options = {},
) => {
  if (!captureExceptionTimeout) {
    captureExceptionTimeout = setTimeout(() => {
      exceptionsCount = 0;
      captureExceptionTimeout = null;
    }, cycleExpires);
  }

  const now = Date.now();

  if (!firstDayErrorTimestamp) {
    firstDayErrorTimestamp = now;
  } else if (now > firstDayErrorTimestamp + dayExpires) {
    firstDayErrorTimestamp = now;
    resetCaptureExceptionRateLimit();
  }

  if (exceptionsCount < maxExceptionsPerCycle && exceptionsDayCount < maxExceptionsPerDay) {
    Sentry.captureException(err, context);
    exceptionsCount += 1;
    exceptionsDayCount += 1;
  }
};

const resetCaptureExceptionRateLimit = () => {
  exceptionsCount = 0;
  exceptionsDayCount = 0;
  if (captureExceptionTimeout) {
    clearTimeout(captureExceptionTimeout);
    captureExceptionTimeout = null;
  }
};
